/**
 * React hooks of the Lines feature (W2-07). The panel lives inside the editor shell, but its dialogs are hosted at
 * the app level (outside the DocStoreProvider) and the status-bar host sits in the hint bar, so every hook reads the
 * store, the editor state and the settings through `services` instead of context.
 */

import { useCallback, useSyncExternalStore } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { Json, Unsubscribe } from '../../core/util/types';
import { useExternalSelector } from '../../state/react';
import { linesUi, type LinesUiState } from './linesUi';
import { nowTracker, type NowState } from './nowState';
export { useServicesStore, useDocValue, useEditorValue } from '../../app/registry/react';

/** The Lines session UI state (view-independent parts). */
export function useLinesUi<T>(services: AppServices, selector: (s: LinesUiState) => T): T {
  const ui = linesUi(services);
  const subscribe = useCallback((cb: () => void): Unsubscribe => ui.subscribe(cb), [ui]);
  const get = useCallback((): LinesUiState => ui.get(), [ui]);
  return useExternalSelector(ui, subscribe, get, selector);
}

/** A feature preference (settings.ext); re-renders when settings change. */
export function useExtSetting(services: AppServices, key: string): Json | undefined {
  const settings = services.settings;
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe =>
      settings.subscribe(() => {
        cb();
      }),
    [settings],
  );
  const get = useCallback((): Json | undefined => settings.getExt(key), [settings, key]);
  return useSyncExternalStore(subscribe, get, get);
}

/** Visible-now / animating-now facts at the playhead (polled while mounted). */
export function useNow(services: AppServices, enabled: boolean): NowState | null {
  const tracker = nowTracker(services);
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe => (enabled ? tracker.subscribe(cb) : () => undefined),
    [tracker, enabled],
  );
  const get = useCallback((): NowState | null => (enabled ? tracker.get() : null), [tracker, enabled]);
  return useSyncExternalStore(subscribe, get, get);
}

/** UI scale of the user settings (row heights of virtualised lists). */
export function useUiScale(services: AppServices): number {
  const settings = services.settings;
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe =>
      settings.subscribe(() => {
        cb();
      }),
    [settings],
  );
  const get = useCallback((): number => {
    const v = settings.value.general.uiScale;
    return Number.isFinite(v) && v > 0 ? v : 1;
  }, [settings]);
  return useSyncExternalStore(subscribe, get, get);
}

/** Element-wise identity comparison of arrays (records are immutable; the document's tables are mutated in place). */
export function sameItems<T>(a: readonly T[], b: readonly T[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (!Object.is(a[i], b[i])) return false;
  return true;
}
